import _ from 'lodash'
import { useQuery } from 'react-query'

import { getIsAuthorized } from './AccountProvider'
import getClient from './http-common'

// Requests

const getAccountDetails = () => getClient().get('/v1/accounts/me')

const getAccountWithPaymentProfile = () =>
  getClient().get('/v1/accounts/me?expand=paymentProfile')

const getBidProfile = () => getClient().get('/v1/accounts/me/bidprofiles')

const getPerson = () => getClient().get('/v1/people/me')

const getSegments = () => getClient().get('/v1/segments?expand=industryCodes')

const getBusinessProfile = () =>
  getClient().get('/v1/accounts/me/businessprofiles')

const getEmailAddress = () => getClient().get('/v1/emails/email/me')

const getPersonWithPhone = () =>
  getClient().get('/v1/people/me?expand=phoneNumbers')

const getSavedSearches = () => getClient().get('/v1/savedsearch/me/searches')

const getPersonWithAddresses = () =>
  getClient().get('/v1/people/me?expand=addresses')

const getNewItems = ({ queryKey }) => {
  const [_, lastLoginTime] = queryKey
  const formatedTime = lastLoginTime.replace(' ', 'T')
  return getClient().get(
    `/v1/search/newitems/${new Date(formatedTime)
      .getTime()
      .toString()
      .substring(0, 10)}`
  )
}

const getTaxonomy = () => getClient().get('/v1/taxonomy/retrieve')

const getStates = ({ queryKey }) => {
  const [_, countryId] = queryKey
  return getClient().get(`/v1/states?countryId=${countryId}&limit=1000`)
}

const getState = ({ queryKey }) => {
  const [_, stateId] = queryKey
  return getClient().get(`/v1/states/${stateId}`)
}

const getAccountWithBank = () =>
  getClient().get('/v1/accounts/me?expand=bankReferences')

/***************************************************************************/

// Queries

// Account information: id, emailId, employee info, personId
const accountPlaceholder = {
  id: '',
  lastLoginTime: '',
}

export const useAccountQuery = () => {
  const query = useQuery('accounts', getAccountDetails, {
    enabled: getIsAuthorized(),
    retry: 0,
  })
  return mergeQueryData(accountPlaceholder, query)
}

// Bid information: statusId
export const useBidProfileQuery = () => {
  const query = useQuery('bidProfile', getBidProfile, {
    enabled: getIsAuthorized(),
    retry: 0,
  })
  return { ...query, data: query.data?.data }
}

// Person information: firstName, lastName, accountId
const personPlaceholder = {
  firstName: '',
  lastName: '',
}

export const usePersonQuery = () => {
  const query = useQuery('person', getPerson, {
    enabled: getIsAuthorized(),
    retry: 0,
  })
  return mergeQueryData(personPlaceholder, query)
}

// Segment information: industries and industry codes/interests
const segmentPlaceholder = [
  { id: 0, industryCodes: [''], industry_code: 0, title: '' },
  { id: 1, industryCodes: [''], industry_code: 0, title: '' },
  { id: 2, industryCodes: [''], industry_code: 0, title: '' },
]

export const useSegmentsQuery = () => {
  const query = useQuery('segments', getSegments, {
    staleTime: Infinity,
    cacheTime: Infinity,
  })
  return mergeQueryData(segmentPlaceholder, query)
}

// Email information: id, address
const emailPlaceholder = {
  address: '',
}

export const useEmailQuery = () => {
  const query = useQuery('email', getEmailAddress, {
    enabled: getIsAuthorized(),
    retry: 0,
  })
  return mergeQueryData(emailPlaceholder, query)
}

// Person information + phone numbers
const personWithPhonePlaceholder = {
  id: 0,
  phoneNumbers: [''],
  firstName: '',
  lastName: '',
}

export const usePersonWithPhoneQuery = () => {
  const query = useQuery('personWithPhone', getPersonWithPhone, {
    enabled: getIsAuthorized(),
  })
  return mergeQueryData(personWithPhonePlaceholder, query)
}

// Business information
const businessProfilePlaceholder = {
  companyName: '',
  position: '',
  segmentId: '',
  industryCodes: [''],
}

export const useBusinessQuery = () => {
  const query = useQuery('businessProfile', getBusinessProfile, {
    enabled: getIsAuthorized(),
    retry: 0,
  })

  let cleanQuery = query
  const innerData = query?.data?.data

  if (query.data) {
    cleanQuery = {
      ...query,
      data: {
        data: innerData.length ? innerData[innerData.length - 1] : innerData,
      },
    }
  }
  return mergeQueryData(businessProfilePlaceholder, cleanQuery)
}

// Saved searches
export const useSavedSearchesQuery = (onSuccess = () => {}) => {
  const query = useQuery('savedsearches', getSavedSearches, {
    onSuccess: data => onSuccess(data),
    enabled: getIsAuthorized(),
    staleTime: 0,
  })
  return { ...query, data: query.data?.data }
}

// New items
const itemsPlaceholder = []

export const useNewItemsQuery = (lastLoginTime, enabled) => {
  const query = useQuery(['newitems', lastLoginTime], getNewItems, {
    staleTime: Infinity,
    enabled: enabled && getIsAuthorized(),
  })
  // console.log('query', query)
  return mergeQueryData(itemsPlaceholder, query)
}

const taxonomyPlaceholder = []

export const useTaxonomyQuery = () => {
  const query = useQuery(['taxonomy', getClient], getTaxonomy, {
    staleTime: Infinity,
  })
  return mergeQueryData(taxonomyPlaceholder, query)
}

export const usePersonWithAddressesQuery = () => {
  const query = useQuery('addresses', getPersonWithAddresses)
  // const result = mergeQueryData(personWithAddressesPlaceholderData, query)
  return { ...query, data: query.data?.data }
}

export const useStatesQuery = (countryId, options) => {
  const query = useQuery(['states', countryId], getStates, {
    staleTime: Infinity,
    ...options,
  })
  return { ...query, data: query.data?.data }
}

export const useStateQuery = (stateId, options) => {
  const query = useQuery(['state', stateId], getState, {
    staleTime: Infinity,
    ...options,
  })
  return { ...query, data: query.data?.data }
}

export const useAccountWithPaymentProfileQuery = () => {
  const query = useQuery('paymentProfile', getAccountWithPaymentProfile)
  return { ...query, data: query.data?.data }
}

export const useAccountWithBank = () => {
  const query = useQuery('accountWithBank', getAccountWithBank)
  return { ...query, data: query.data?.data }
}

/**
 * * Merges query data when it arrives into placeholder data.
 * @param {Array<any> | object} placeholderData
 * @param {object} query
 * @returns {object}
 */
const mergeQueryData = (placeholderData, query) => {
  const data = _.mergeWith(placeholderData, query?.data?.data, (obj, src) =>
    _.isNil(src) ? obj : src
  )
  return { ...query, data }
}
